/**
 * Request detail modal — full submission, calendar-conflict banner, status
 * workflow actions (server-enforced transition map mirrored client-side),
 * internal notes, status history, email delivery status + resend.
 */

import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner, StatusPill } from '../ui';
import {
  addTakeoverNote,
  changeTakeoverStatus,
  getTakeoverRequest,
  resendTakeoverNotifications,
} from '../../services/takeoversApi';
import { STATUS_VARIANT, fmtDateTime, fmtWindow } from './shared';
import {
  OverlappingApproved,
  STATUS_LABELS,
  STATUS_NEXT,
  TakeoverRequest,
  TakeoverRequestStatus,
} from './types';

interface RequestDetailProps {
  requestId: string;
  canManage: boolean;
  onClose: () => void;
  /** Called with the updated request after any mutation so the table refreshes. */
  onUpdated: (request: TakeoverRequest) => void;
  notify: (kind: 'success' | 'error', title: string, message?: string) => void;
}

const label: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  marginBottom: 4,
};

const Field: React.FC<{ name: string; children: React.ReactNode; span2?: boolean }> = ({ name, children, span2 }) => (
  <div style={span2 ? { gridColumn: 'span 2' } : undefined}>
    <div style={label}>{name}</div>
    <div style={{ fontSize: 14, color: 'var(--fg-1)' }}>{children}</div>
  </div>
);

const RequestDetail: React.FC<RequestDetailProps> = ({ requestId, canManage, onClose, onUpdated, notify }) => {
  const [request, setRequest] = useState<TakeoverRequest | null>(null);
  const [overlapping, setOverlapping] = useState<OverlappingApproved[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [declineNote, setDeclineNote] = useState('');
  const [confirmingDecline, setConfirmingDecline] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTakeoverRequest(requestId)
      .then((res) => {
        if (cancelled) return;
        setRequest(res.request);
        setOverlapping(res.overlappingApproved || []);
        setError(null);
      })
      .catch((err) => !cancelled && setError(err?.message || 'Failed to load request'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [requestId]);

  const applyUpdate = (updated: TakeoverRequest) => {
    setRequest(updated);
    onUpdated(updated);
  };

  const moveTo = async (to: TakeoverRequestStatus, note?: string) => {
    if (!request) return;
    setBusy(true);
    try {
      const updated = await changeTakeoverStatus(request.id, to, note);
      applyUpdate(updated);
      notify('success', `Moved to ${STATUS_LABELS[to]}`);
      setConfirmingDecline(false);
      setDeclineNote('');
    } catch (err: any) {
      notify('error', 'Status change failed', err?.message);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!request || !noteText.trim()) return;
    setBusy(true);
    try {
      const updated = await addTakeoverNote(request.id, noteText.trim());
      applyUpdate(updated);
      setNoteText('');
    } catch (err: any) {
      notify('error', 'Failed to add note', err?.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!request) return;
    setBusy(true);
    try {
      await resendTakeoverNotifications(request.id);
      notify('success', 'Resend started', 'Delivery status updates in a few seconds.');
    } catch (err: any) {
      notify('error', 'Resend failed', err?.message);
    } finally {
      setBusy(false);
    }
  };

  const nextStatuses = request ? STATUS_NEXT[request.status] ?? [] : [];
  const utmEntries = Object.entries(request?.utm ?? {}).filter(([, v]) => v);

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Takeover request"
      title={request ? `${request.brand.name} → ${request.creatorDisplayName}` : 'Loading…'}
      width={720}
      closeOnEsc={false}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
      ) : error || !request ? (
        <div style={{ color: 'var(--danger)', padding: 20 }}>{error || 'Request not found'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {overlapping.length > 0 && (
            <div
              style={{
                background: 'rgba(245,165,36,0.12)',
                border: '1px solid rgba(245,165,36,0.4)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 14px',
                fontSize: 13,
              }}
            >
              <strong>⚠️ Calendar conflict:</strong> {overlapping.length} approved/booked takeover
              {overlapping.length > 1 ? 's overlap' : ' overlaps'} this window —{' '}
              {overlapping.map((o) => `${o.brand} (${fmtWindow(o.window)})`).join(' · ')}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <StatusPill variant={STATUS_VARIANT[request.displayStatus]}>
              {STATUS_LABELS[request.displayStatus]}
            </StatusPill>
            <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>
              Received {fmtDateTime(request.createdAt)}
              {request.meta.turnstileVerified ? ' · verified ✓' : ' · unverified'}
            </span>
            <div style={{ flex: 1 }} />
            {canManage &&
              nextStatuses
                .filter((s) => s !== 'declined')
                .map((s) => (
                  <Button key={s} size="sm" variant="primary" disabled={busy} onClick={() => moveTo(s)}>
                    Move to {STATUS_LABELS[s]}
                  </Button>
                ))}
            {canManage && nextStatuses.includes('declined') && (
              <Button size="sm" variant="subtle" disabled={busy} onClick={() => setConfirmingDecline(true)}>
                Decline
              </Button>
            )}
          </div>

          {confirmingDecline && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                value={declineNote}
                onChange={(e) => setDeclineNote(e.target.value)}
                placeholder="Reason (optional, kept internal)"
                style={{ flex: 1, padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-1)', fontSize: 13 }}
              />
              <Button size="sm" variant="destructive" disabled={busy} onClick={() => moveTo('declined', declineNote || undefined)}>
                Confirm decline
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmingDecline(false)}>Cancel</Button>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field name="Brand">
              {request.brand.name} ·{' '}
              <a href={request.brand.website} target="_blank" rel="noreferrer">{request.brand.website}</a>
            </Field>
            <Field name="Contact">
              {request.contact.name} · <a href={`mailto:${request.contact.email}`}>{request.contact.email}</a>
              {request.contact.phone ? ` · ${request.contact.phone}` : ''}
            </Field>
            <Field name="Budget">{request.budgetTierLabel}</Field>
            <Field name="Requested window">{fmtWindow(request.window)}</Field>
            {request.notes && <Field name="Campaign goals / notes" span2>{request.notes}</Field>}
            {request.briefLink && (
              <Field name="Brief" span2>
                <a href={request.briefLink} target="_blank" rel="noreferrer">{request.briefLink}</a>
              </Field>
            )}
            {utmEntries.length > 0 && (
              <Field name="Attribution" span2>
                <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                  {utmEntries.map(([k, v]) => `${k}=${v}`).join(' · ')}
                </span>
              </Field>
            )}
            <Field name="Notifications" span2>
              <span style={{ fontSize: 13 }}>
                Advertiser: {request.emailStatus.advertiser} · Creator: {request.emailStatus.creator} · Internal: {request.emailStatus.internal}
              </span>
              {canManage && (
                <Button size="sm" variant="ghost" disabled={busy} onClick={resend} style={{ marginLeft: 10 }}>
                  Resend
                </Button>
              )}
            </Field>
          </div>

          <div>
            <div style={label}>Internal notes</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {request.internalNotes.length === 0 && (
                <span style={{ fontSize: 13, color: 'var(--fg-4)' }}>No notes yet.</span>
              )}
              {request.internalNotes.map((n) => (
                <div key={n.id} style={{ background: 'var(--bg-surface-2)', borderRadius: 'var(--radius-md)', padding: '8px 12px' }}>
                  <div style={{ fontSize: 13 }}>{n.text}</div>
                  <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 2 }}>{n.byEmail} · {fmtDateTime(n.at)}</div>
                </div>
              ))}
              {canManage && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addNote()}
                    placeholder="Add an internal note…"
                    style={{ flex: 1, padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-1)', fontSize: 13 }}
                  />
                  <Button size="sm" variant="subtle" disabled={busy || !noteText.trim()} onClick={addNote}>
                    Add
                  </Button>
                </div>
              )}
            </div>
          </div>

          {request.statusHistory.length > 0 && (
            <div>
              <div style={label}>Status history</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {[...request.statusHistory].reverse().map((h, i) => (
                  <div key={i} style={{ fontSize: 12.5, color: 'var(--fg-3)' }}>
                    {fmtDateTime(h.at)} — <strong>{STATUS_LABELS[h.from]}</strong> → <strong>{STATUS_LABELS[h.to]}</strong> by {h.byEmail}
                    {h.note ? ` — “${h.note}”` : ''}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default RequestDetail;
